import { createQueryParams } from '@/_utils/createQueryParams';
import { encodeURIParamsLegacy } from '@/_utils/encode-uri-params';
import { BagPaymentListApiService } from '@/bag/bag-payment-list/bag-payment-list-api.service';
import {
	BagPaymentBankAccount,
	BagPaymentFilters,
	BagPaymentListTableColumn,
	BagPaymentParameters,
	BagPaymentTableItem,
} from '@/bag/bag-payment-list/bag-payment-list.model';
import { Data2Service } from '@/common/data2.service';
import { DownloadFileService } from '@/common/download-file.service';
import { PaymentStatus, paymentStatusLabelMappings } from '@/common/enums/paymentStatus';
import { Status, statusMappings } from '@/common/enums/Status';
import { LoaderService } from '@/common/loader/loader.service';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { firstValueFrom, forkJoin } from 'rxjs';

@Component({
	selector: 'app-bag-payment-list',
	templateUrl: './bag-payment-list.component.html',
	styleUrls: ['./bag-payment-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagPaymentListComponent implements OnInit {
	readonly data = signal<BagPaymentTableItem[]>([]);

	readonly parameters = signal<BagPaymentParameters>({
		bankAccounts: [],
		status: [
			{ id: PaymentStatus.IN_PROGRESS, description: paymentStatusLabelMappings[PaymentStatus.IN_PROGRESS] },
			{ id: PaymentStatus.COMPLETED, description: paymentStatusLabelMappings[PaymentStatus.COMPLETED] },
			{ id: PaymentStatus.CANCELED, description: paymentStatusLabelMappings[PaymentStatus.CANCELED] },
		],
		paymentKinds: [],
		reservationStatus: [
			{ id: Status.OPTION, name: statusMappings[Status.OPTION] },
			{ id: Status.CONFIRMED, name: statusMappings[Status.CONFIRMED] },
			{ id: Status.CANCELED, name: statusMappings[Status.CANCELED] },
			{ id: Status.REJECTED, name: statusMappings[Status.REJECTED] },
		],
		currency: [],
	});

	readonly paymentColumns: BagPaymentListTableColumn[] = [
		{ id: 'payment', name: 'Płatność' },
		{ id: 'posting', name: 'Księgowanie' },
		{ id: 'status', name: 'Status' },
		{ id: 'kind', name: 'Rodzaj' },
		{ id: 'amount', name: 'Kwota' },
		{ id: 'payer', name: 'Płatnik' },
		{ id: 'bankAccountPosting', name: 'Konto wpłaty' },
		{ id: 'bankAccountIncoming', name: 'Konto płatnika' },
		{ id: 'invoiceNumber', name: 'Numer faktury' },
		{ id: 'tableId', name: 'Numer rezerwacji' },
		{ id: 'reservationStatus', name: 'Status rezerwacji' },
	];

	readonly page = signal(0);
	readonly pageSize = signal(50);
	readonly allItemsSize = signal(0);
	readonly summaryTotalAmount = signal(0);
	readonly paymentCache = signal<Record<number, BagPaymentTableItem[]>>({});
	readonly ordering = signal<string | null>(null);

	private readonly api = inject(Data2Service);
	private readonly loader = inject(LoaderService);
	private readonly bagPaymentListApiService = inject(BagPaymentListApiService);
	private readonly downloadFileService = inject(DownloadFileService);
	private readonly destroyRef = inject(DestroyRef);

	private filters: BagPaymentFilters | undefined;
	private parametersLoaded = false;
	private listLoaded = false;

	ngOnInit(): void {
		this.loader.show();
		this.loadParameters();
	}

	filtersSelected(filters: BagPaymentFilters): void {
		this.filters = filters;
		this.paymentCache.set({});
		this.page.set(0);
		this.allItemsSize.set(0);
		this.listLoaded = false;
		this.loader.show();
		this.loadData();
		this.loadSummary();
	}

	pageChanged(page: PageEvent): void {
		if (page.pageSize !== this.pageSize()) {
			this.paymentCache.set({});
		}
		this.page.set(page.pageIndex);
		this.pageSize.set(page.pageSize);
		this.loadData();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	onSortChanged(ordering: string | null): void {
		this.ordering.set(ordering);
		this.paymentCache.set({});
		this.page.set(0);
		this.loadData();
	}

	paymentsChanged(): void {
		this.paymentCache.set({});
		this.listLoaded = false;
		this.loader.show();
		this.loadData();
		this.loadSummary();
	}

	exportCsv(): void {
		if (!this.filters) {
			return;
		}

		const filtersWithoutPagination: BagPaymentFilters = { ...this.filters };
		delete filtersWithoutPagination.limit;
		delete filtersWithoutPagination.offset;

		const params = encodeURIParamsLegacy(this.api.getChecked(filtersWithoutPagination, this.parameters()));

		this.bagPaymentListApiService
			.getPaymentsCsv(params)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((blob) => {
				this.downloadFileService.downloadBlob(blob, 'platnosci.csv');
			});
	}

	private loadParameters(): void {
		const limitQuery = createQueryParams({}, '999');

		firstValueFrom(
			forkJoin({
				bankAccounts: this.bagPaymentListApiService.getBankAccounts(limitQuery),
				paymentKinds: this.bagPaymentListApiService.getPaymentKinds(limitQuery),
				currency: this.bagPaymentListApiService.getCurrencies(limitQuery),
			}),
		)
			.then((result) => {
				this.parameters.set({
					...this.parameters(),
					bankAccounts: this.normalizeBankAccounts(result.bankAccounts),
					paymentKinds: result.paymentKinds.map((kind) => ({
						...kind,
						id: kind.id.toString(),
					})),
					currency: result.currency.map((currency) => ({
						...currency,
						id: currency.id.toString(),
					})),
				});
				this.parametersLoaded = true;
				this.maybeHideLoader();
			})
			.catch(() => {
				this.loader.hide();
			});
	}

	private loadData(): void {
		if (!this.filters) {
			return;
		}

		this.filters.limit = this.pageSize();
		this.filters.offset = this.page() * this.pageSize();
		this.filters.ordering = this.ordering();

		const cachedData = this.paymentCache()[this.page()];
		if (cachedData) {
			this.data.set(cachedData);
			this.listLoaded = true;
			this.maybeHideLoader();
			return;
		}

		this.loader.show();
		const params = encodeURIParamsLegacy(this.api.getChecked(this.filters, this.parameters()));

		firstValueFrom(this.bagPaymentListApiService.getPayments(params))
			.then((result) => {
				const rows = result.results ?? [];
				this.allItemsSize.set(result.count);
				this.data.set(rows);
				this.paymentCache.set({ ...this.paymentCache(), [this.page()]: rows });
				this.listLoaded = true;
				this.maybeHideLoader();
			})
			.catch(() => {
				this.loader.hide();
			});
	}

	private loadSummary(): void {
		if (!this.filters) {
			return;
		}

		const filtersWithoutPagination: BagPaymentFilters = { ...this.filters };
		delete filtersWithoutPagination.limit;
		delete filtersWithoutPagination.offset;

		const params = encodeURIParamsLegacy(this.api.getChecked(filtersWithoutPagination, this.parameters()));

		firstValueFrom(this.bagPaymentListApiService.getPaymentsAmount(params)).then((result) => {
			this.summaryTotalAmount.set(result.totalAmount);
		});
	}

	private maybeHideLoader(): void {
		if (this.parametersLoaded && this.listLoaded) {
			this.loader.hide();
		}
	}

	private normalizeBankAccounts(bankAccounts: BagPaymentBankAccount[]): BagPaymentBankAccount[] {
		return bankAccounts.map((bankAccount) => ({
			...bankAccount,
			id: bankAccount.id.toString(),
		}));
	}
}
